import type { AddStage } from '#~/device/commands/types.js';
import type { OnboardingAddMethod } from '#~/analytics/constants/onboarding-events.js';
import type { ChildrenOnly } from '@studio/design-system/props';
import type { Agent } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import type { Protocol as ControlProtocol } from '@studio/models/control';
import type { Brand, Device, Model } from '@studio/models/device';
import type { Token } from '@studio/models/livekit';
import type { Room } from '@studio/models/room';
import type { Protocol as VideoProtocol } from '@studio/models/video';
import type * as S from '@studio/react-utils/store';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import type { UseBoundStoreWithEqualityFn } from 'zustand/traditional';
import type {
  Collection as Discoveries,
  Discovery,
  Discovery$Control,
  Discovery$Video
} from '#~/device/discovery/index.js';
import type { Aspects, Device$Video, Collection as Devices, Minimist } from '#~/device/index.js';
import type { Template } from '#~/device/registry/index.js';
import type { PreviewAvailability } from './preview-outcome.js';

/** Mutable data draft. */
export type Draft = S.Draft<State>;

/**
 * Discriminated reason for a failed bulk-add device — Multi-Add Phase 2 W3
 * (PRD §2.11). Each value maps to a chip secondary-text string via the
 * resolver in `left-tray/chip.tsx`. See `failed-devices.slice.ts` for the
 * mutation API.
 *
 *   auth                — camera rejected creds (retry probe + commit failed)
 *   locked              — Iris-enabled device rate-limited / device-login locked
 *   already-linked      — Iris-enabled device already part of another studio
 *   commit-rejected     — Promise.allSettled rejected (bridge/network/etc.)
 *   bridge-disambiguation — no agentId resolvable (multi-bridge scenario)
 *   error               — generic / buildBulkCandidate refused
 *
 * Per `code-comments-required` (ai.knowledge/global): the enum's values
 * are the canonical strings the chip resolver pattern-matches on. Adding
 * a new value MUST add a matching copy mapping.
 */
export type FailedDeviceReason =
  | 'auth'
  | 'locked'
  | 'already-linked'
  | 'commit-rejected'
  | 'bridge-disambiguation'
  | 'error';

/**
 * Per-entry failed-device record — Multi-Add Phase 2 W3 (PRD §2.11).
 *
 * Holds the minimum needed for the failed-tile placeholder to render +
 * for the Retry button to re-invoke the single-add path:
 *   - `deviceId` — stable id captured at commit-attempt time
 *   - `name` — display string for the gray-tile primary label
 *   - `reason` — discriminator for the secondary chip copy
 *   - `discovery` — original snapshot so the Retry can rebuild the
 *     single-add candidate without re-querying discovery
 *   - `failedAt` — wall-clock for sort/debugging
 */
export interface FailedDeviceEntry {
  deviceId: string;
  name: string;
  reason: FailedDeviceReason;
  // The discovery snapshot stored verbatim. Discovery is a wide union —
  // we keep it as the same `Discovery` import used by the rest of the
  // store so the Retry consumer can re-fingerprint via the existing
  // `buildBulkCandidate` helper. Per `think-in-systems` — store stays
  // structural; chip resolver / Retry composer are pure functions.
  discovery: Discovery;
  failedAt: number;
}

/** Preview device. */
export interface Preview {
  /** Device ID or `false` on error. */
  deviceId: Device['id'];

  /** Error flag. */
  error?: T_Maybe<Error>;

  /** URX. */
  urx: string;
}

/** Immer producer. */
export type Producer = S.Producer<State>;

/** `<Provider>` properties. */
export type Props = ChildrenOnly & {
  agentIds: Agent['id'][];
  appId: AppId;
  devices: Devices;
  discoveries: Discoveries;
  /**
   * First-run funnel control surface. Omitted everywhere except onboarding, so
   * the normal Add Video Source workflow is unchanged by construction.
   */
  onboarding?: OnboardingHandoff | undefined;
  roomId: Room['id'];
};

/**
 * Lifecycle status of the bulk-add commit pipeline.
 *
 * Per the bulk-add design and Multi-Add Phase 1 manual QA (ONVIF
 * sequential probe + pause-for-auth flow): the bulk pipeline runs
 * sequentially through the user's selection and can pause when a
 * source requires authentication that the discovery didn't carry.
 * The status drives a progress overlay rendered by `<Scaffold>` and
 * the `<OnvifAuthPrompt>` modal — both react to discriminated unions
 * here instead of separate flags so the legal transitions are obvious
 * from the type.
 *
 *   idle                 — pipeline not running
 *   preparing            — committed click; gathering plans, no per-
 *                          device work yet
 *   configuring          — probing / committing source #i of N; UI
 *                          shows spinner + "Configuring {name}…"
 *   awaiting-auth        — paused on a source that needs credentials;
 *                          modal collects them, on submit the pipeline
 *                          resolves the deferred promise and resumes
 *   committing           — all preflight done, addDevice calls in
 *                          flight; spinner "Adding {N} sources…"
 *   done                 — pipeline complete; outer flow toasts +
 *                          closes the modal
 */
export type BulkAddStatus =
  | { kind: 'idle' }
  | { kind: 'preparing' }
  | {
      kind: 'configuring';
      name: string;
      index: number; // 1-based
      total: number;
    }
  | {
      kind: 'awaiting-auth';
      name: string;
      index: number;
      total: number;
      // Resolver set by the pipeline; the modal calls it with the
      // user's credentials or `null` to skip. Stored in the slice so
      // the modal — rendered by Scaffold via a state subscription —
      // can resolve the pending probe step without prop-drilling.
      resolve: (
        decision: { credentials: { username: string; password: string } } | { skip: true }
      ) => void;
      /**
       * Optional discriminator for the prompt copy strings —
       * Multi-Add Phase 2 (PRD §2.8 — Prompt wording refinement).
       *
       * Forward-compat hook: when wiring.ts evolves to set this on the
       * Iris-enabled `setStatus({kind:'awaiting-auth',...})` call (the
       * always-prompt branch), the prompt renders the generic
       * "Iris-enabled device" caveat. Until then, scaffold.tsx derives
       * the kind by name-matching the paused discovery in the
       * `bulkDiscoveries` mirror and reading `irisEnabled` — see
       * `deriveOnvifAuthPromptKind` in
       * `source/dialogs/onvif-auth-prompt-kind.ts`.
       *
       * Default (omitted) keeps the Multi-Add Phase 1 ONVIF retry call
       * site unchanged — backward-compatible.
       */
      promptKind?: 'onvif' | 'iris-enabled';
    }
  | { kind: 'committing'; total: number }
  | { kind: 'done' };

/**
 * Everything the first-run funnel needs to take over the workflow after the
 * model step, handed over as the panels' own state rather than as onboarding
 * vocabulary.
 *
 * The shared panel deliberately knows NOTHING about what onboarding does with
 * this. It reports what it holds; `onboarding/add-handoff.ts` decides what that
 * means. The other direction — this panel importing onboarding's types to
 * describe its own exit — is the coupling `camera-config.ts` already documents
 * as the wrong way round.
 */
export interface OnboardingExit {
  /** The candidate as configured, including the resolved video settings. */
  readonly candidate: State['candidate'];

  /** The Bridge that will perform the add. */
  readonly agentId: Agent['id'];

  /** The discovery the user picked, or undefined for a manually typed camera. */
  readonly selection: T_Maybe<Discovery>;

  /** The chosen model's registry template, the only source of a control endpoint. */
  readonly template: T_Maybe<Template>;

  /** The model's default device name, for the "PTZOptics Move 4K" line. */
  readonly defaultName: T_Maybe<string>;

  /**
   * The device the workflow's own commit CREATED.
   *
   * The funnel's connecting screen watches this device rather than adding a
   * second one. Adding twice is what produced a camera with no control driver
   * and an empty host: the second add rebuilt a configuration the workflow had
   * already resolved correctly.
   */
  readonly deviceId: T_Maybe<Device['id']>;
}

/**
 * The first-run funnel's control surface over this workflow, or `undefined` for
 * every other caller.
 *
 * `undefined` is the ONLY value production ever passes, so every branch guarded
 * on it is dead outside onboarding — which is what keeps "we do not impact the
 * existing connection stuff" checkable rather than asserted.
 */
export interface OnboardingHandoff {
  /**
   * Replaces the Control panel. Called from the model step's Continue instead
   * of advancing to `Panel.Control`, because in onboarding the control step is
   * the funnel's own connection test.
   */
  readonly onConfigured: (exit: OnboardingExit) => void;

  /** Report a panel the user has arrived at, so the funnel stays countable. */
  readonly onPanelView?: ((panel: Panel) => void) | undefined;

  /**
   * Panel to open on. `Panel.Video` is the manual-entry entry point — the same
   * place the Discover footer's "Manually Configure an RTSP source" link lands —
   * so the funnel's own "Enter manually" button opens the box the user asked
   * for rather than the scan they declined.
   */
  readonly initialPanel?: Panel | undefined;
}

/** Retained across panel remounts: a pending or uncertain write cannot be resent. */
export interface FinalSave {
  deviceId: Device['id'];
  stage: AddStage;
  status: 'pending' | 'retryable' | 'uncertain' | 'saved' | 'superseded';
  rawPending: boolean;
  snapshot: Pick<
    State,
    'candidate' | 'manufacturer' | 'agentId' | 'roomId' | 'flowId' | 'addMethod' | 'onboarding'
  > & {
    selection: State['source']['selection'];
    /** Commit-only settings survive retry without triggering preview setup. */
    commitOptions?: import('../context.js').CommitOptions | undefined;
  };
}

/** Store state. */
export type State = Readonly<{
  /** N11 showing survives navigation from Discover into manual entry. */
  discoveryWarning?: import('#~/analytics/domains/alert-lifecycle.js').AlertShowing | undefined;

  /** Entry choice, independent of the selected video protocol. */
  addMethod?: OnboardingAddMethod;

  finalSave?: FinalSave;

  /** Application ID. */
  appId: AppId;

  /**
   * Skip-credentials flag (see add/source/auth-verdict.ts). Persistent across
   * reactive recomputes so BOTH the control- and video-plane known-model
   * INVALID sites in discovery.tsx honor a single operator Skip (the flag must
   * be store-level, not component-local, for the both-planes case — impl-spec
   * §4 risk 1). Set true by the modal's Skip button; reset false in panel.tsx
   * `handleSelectionChange` when a different discovery row is picked (AC#5).
   */
  authSkipped: boolean;

  /** Candidate device. */
  candidate: Omit<Minimist, 'agentId' | 'id'>;

  /** Control panel state. */
  control: {
    /** Cached configs. */
    cache: T_Optional<{ [K in ControlProtocol]: Minimist['control'] }>;

    /** Ready flag. */
    isReady: boolean;
  };

  /** Flow ID for tracking multi-step workflow */
  flowId: string;

  /** External state. */
  external: {
    transients: Devices;
  };

  /** Manufacturer panel state. */
  manufacturer: T_Optional<{
    /** Device configuration. */
    config: Template;

    /** Default device name. */
    defaultName: string;
  }> & {
    /** Ready flag. */
    isReady: boolean;

    /** Preferred model ID (per brand). */
    preferredModelId: Record<Brand['id'], T_Maybe<Model['id']>>;
  };

  /**
   * First-run funnel control surface, or undefined for the normal workflow.
   * Read by the model step's footer and by the scaffold; nothing else branches.
   */
  onboarding: T_Maybe<OnboardingHandoff>;

  /** Selected panel. */
  panel: Panel;

  /** Preview devices. */
  previews: T_Optional<Record<keyof Aspects, Preview>>;

  /** Increments on Retry preview so a stale generation cannot write. */
  previewGeneration: number;

  /** Add-scoped preview availability. Not a Continue input. */
  previewAvailability: PreviewAvailability;

  /** Safe inline copy for prepare/unavailable. */
  previewCopy?: string | undefined;

  /** Raw preview setup still in flight. Retry stays disabled while true. */
  previewPending: boolean;

  /** Save or close owns cleanup; preview setup stays suspended until reopen. */
  previewPaused?: boolean;

  /** This configuration completed setup; retained old records cannot prove video. */
  previewReady?: boolean;
  /**
   * Preview adds the Mediator has sent for the current candidate and the Bridge has not
   * answered yet, by aspect. Set right before the add goes out, cleared when it settles
   * (`previews` then holds the outcome) or when the candidate's URX for that aspect
   * changes, since that add is then not this candidate's preview. discovery.tsx starts
   * the RTSP (ONVIF) stall budget from the control entry.
   */
  pendingPreviews: T_Optional<Record<keyof Aspects, Pick<Preview, 'deviceId' | 'urx'>>>;

  /** Immer producer. */
  produce: Producer;

  /** Source panel state. */
  source: {
    /** Discovered devices. */
    discoveries: (Discovery$Control | Discovery$Video)[];

    /**
     * Per-room failed-device slice — Multi-Add Phase 2 (PRD §2.11).
     *
     * Devices that NEVER committed (Promise.allSettled rejected, agentId
     * unresolved, build-candidate refused) have no state-sync entry, so
     * the chip/tile surfaces that subscribe to depot can't render them.
     * This slice is the parallel-store primitive: one entry per failed
     * device captured at the point of failure inside `runBulkCommit`.
     *
     * Scope is per-room (cleared on room-leave by the Provider); per
     * `think-in-systems` (ai.knowledge/global) it's a separate primitive
     * from `multiSelection` / `bulkAddStatus` so the failed-tile renderer
     * and the slice mutations stay independently testable.
     *
     * Tiles surface from this slice with gray-bg background +
     * Retry/Remove inline buttons (failed-tile placeholder, PRD
     * §2.11). Retry re-invokes the single-add path; on success the
     * entry evicts. Remove evicts without re-attempt.
     *
     * The discovery snapshot is preserved alongside the reason so the
     * Retry path can rebuild a single-add candidate without re-running
     * discovery. `reason` is a discriminated string the chip secondary-
     * text resolver maps to copy ("Authentication failed" / "Device
     * locked — try again in 30s" / "Linked to another studio" / etc.).
     */
    failedDevices: readonly FailedDeviceEntry[];

    /**
     * Discoveries snapshot from the state-query source the bulk-add Table
     * actually renders rows from. Populated by `Selector` when the bulk-add
     * feature flag is on. Bug fix (Multi-Add Phase 1 manual QA):
     * `source.discoveries` above comes from the legacy single-add prop
     * pipeline (provider.tsx ingests `<Add discoveries=...>` from outside
     * the modal), while the table rows come from
     * `useMultiProxyDiscoveryStateQuery` — the two collections have
     * different id conventions. The commit pipeline previously looked up
     * selection ids in `source.discoveries`, so EVERY bulk-add commit hit
     * "discovery not in slice" and the toast "0 of N sources added"
     * fired even though the rows visibly rendered with those ids.
     * wiring.ts reads `bulkDiscoveries` (with fallback to `discoveries`)
     * so the lookup keys match the ids the user actually clicked.
     */
    bulkDiscoveries?: T_Maybe<readonly Discovery[]>;

    /**
     * Device-registry templates indexed by `Model['id']`, mirrored from
     * `useTemplates` in Selector. Populated only when the bulk-add feature
     * flag is on. Bug fix (Multi-Add Phase 1 manual QA — "Bug 6"):
     * `runBulkCommit` previously called `buildBulkCandidate(discovery)`
     * with no template argument, so the known-model branch in
     * `buildBulkCandidate` (which guards on `!!template`) never fired.
     * Fingerprint-matched RTSP discoveries (modelId resolved, brand cell
     * populated like "Everet" for EVPRO2) ended up taking the generic
     * RTSP branch instead — defaulted to VISCA control, no
     * template-driven URX, model "Other" in the resulting tile.
     * `handleBulkCommit` now looks up the per-discovery template from this
     * mirror and threads it into `buildBulkCandidate`, restoring byte-
     * parity with single-add's known-model path.
     */
    bulkTemplates?: T_Maybe<Record<Model['id'], T_Maybe<Template>>>;

    /**
     * Brand-detected resolutions indexed by `Brand['id']`, mirrored from
     * `useModelsByBrands` + `useTemplates` in Selector (extended discovery
     * — Multi-Add). A discovery matched to a BRAND fingerprint but no
     * specific model carries `brandId` with no `modelId`, so `bulkTemplates`
     * (keyed by `modelId`) has no slot for it. Each entry carries the
     * brand's generic "Other" model id + its registry template so
     * `runBulkCommit` can thread `brandDetected` into `buildBulkCandidate`
     * and commit the source as "{Brand} / Other" with the registry URX
     * instead of the blank protocol defaults (rtsp /1 + VISCA 52381).
     * A `template` of `undefined` (still loading / errored) makes the
     * commit fall back to those defaults — no add is lost. Populated only
     * when the bulk-add feature flag is on.
     */
    bulkBrandDetected?: T_Maybe<
      Record<Brand['id'], T_Maybe<{ otherModelId: Model['id']; template: T_Maybe<Template> }>>
    >;

    /** Existing video devices. */
    existing: Device$Video[];

    /** Ready flag. */
    isReady: boolean;

    // Array not Set — enableMapSet() is absent in this repo.
    /** IDs of discoveries checked in multi-select mode. */
    multiSelection: Discovery['id'][];

    /** Whether multi-select bulk-add mode is active. */
    bulkMode: boolean;

    /**
     * Pipeline status for the bulk-add commit flow. Per Multi-Add
     * Phase 1 manual QA (ONVIF sequential probe with inline credentials
     * prompt). See the `BulkAddStatus` discriminated union for the
     * legal states. `kind: 'idle'` when no bulk commit is in flight.
     */
    bulkAddStatus: BulkAddStatus;

    /**
     * Per-batch cancellation flag — Multi-Add Phase 2 (PRD §2.10).
     *
     * Set to `true` by the Cancel button on the `bulk-progress` overlay
     * (`scaffold.tsx`) via `requestCancellation` in `bulk-add.slice.ts`.
     * Cleared back to `false` at the start of every batch in
     * `wiring.ts::handleBulkCommit` so a stale flag from a prior batch
     * cannot short-circuit a fresh run.
     *
     * `runBulkCommit` checks the flag AT THE TOP of every per-device
     * iteration; when `true` the loop breaks immediately, the in-flight
     * step (if any) is allowed to settle, and the pipeline emits
     * `MULTI_ADD_BATCH_CANCELLED` with the partial counts. Already-
     * committed devices stay in the room — `addDevice` / `linkAgent`
     * are not reversible (no rollback per §2.10).
     *
     * Per `think-in-systems` (ai.knowledge/global): a scalar parallel to
     * `bulkAddStatus`, NOT a new BulkAddStatus variant. Cancellation is
     * orthogonal to lifecycle stage — the user can request it during
     * `configuring`, `committing`, or even `awaiting-auth` (the
     * `awaiting-auth` resolver short-circuits via `{ skip: true }`).
     * Folding it into the discriminated union would multiply states
     * combinatorially.
     *
     * Per `reference-rules-in-comments` (ai.knowledge/global).
     */
    cancellationRequested: boolean;

    /** Preferred discovery ID. */
    preferredId?: T_Maybe<Discovery['id']>;

    /**
     * Brand-level fingerprint seed (extended discovery, Change 2B).
     *
     * Set by `panel.tsx::handleSelectionChange` when the user clicks a
     * brand-only discovery row — one whose encoder fingerprint resolved a
     * `brandId` but NO `modelId` (and whose `video.path` is empty, so a
     * preview can never connect). Such a row attempts NO connection
     * (mirroring the Iris-enabled exception); `footer.tsx::handleNext`
     * jumps it straight to `Panel.Video` (the RTSP Helper) — exactly like
     * the manual-RTSP affordance — and `rtsp.tsx` reads this on mount to
     * pre-seed its Brand dropdown, leaving the model for the user to pick.
     *
     * Distinct from `candidate.modelId` (full model match) and from
     * `manufacturer.config` (the resolved template): a brand-only match has
     * neither yet. Cleared back to `undefined` whenever the selection is a
     * non-brand-only row so a stale seed can't leak into a later flow.
     *
     * Per `think-in-systems` (ai.knowledge/global): a dedicated source-slice
     * scalar rather than overloading the `candidate`/`Minimist` shape (which
     * is `M_Device`-derived and carries no `brandId`).
     */
    seededBrandId?: T_Maybe<Brand['id']>;

    /**
     * Brand-only fingerprint seed (Change 2B follow-up, bug1): the discovered
     * HOST for a brand-only match, so the RTSP Helper can PRE-FILL its IP field
     * on mount WITHOUT the connectable `candidate.video` aspect that would
     * otherwise spin up a doomed preview device.
     *
     * The brand-only branch of `panel.tsx::handleSelectionChange` deliberately
     * STRIPS `video`/`control`/`audio` from the synced candidate — a brand-only
     * match has no model/URL yet, so any connectable aspect makes the preview
     * mediator (`store/mediator.tsx`) add a transient encoder device that
     * attempts an RTSP connection to `host` with an empty path → FAILED → the
     * sticky "Connection failed" Callout fires before the user has entered
     * anything (bug1). We stash the host here instead so `rtsp.tsx` seeds the IP
     * field from it without a connection attempt. Cleared (undefined) on every
     * non-brand-only row alongside `seededBrandId`.
     *
     * Per `think-in-systems` (ai.knowledge/global): a dedicated source-slice
     * scalar rather than a partial/connectable `candidate.video`.
     */
    seededHost?: T_Maybe<string>;

    /**
     * The RTSP (ONVIF) probe attempt for the selected row, and whether its stall
     * budget ran out. discovery.tsx starts an attempt when the row is selected or
     * credentials are submitted, and sets `stalled` when the probe gets neither
     * capabilities nor an error within the budget. The budget starts when the
     * Mediator sends the control preview add (`pendingPreviews.control`) and
     * restarts when the Bridge acknowledges it (`previews.control`). That covers an
     * add the Bridge does not answer, which otherwise settles only when its request
     * times out, and a fixed camera whose GetStreamUri failed, which publishes no
     * capabilities.
     *
     * A new attempt replaces the record with `stalled: false`, and a timer only
     * marks the attempt it was started for, so a stall never outlives its attempt.
     * Read by discovery.tsx (badge, credentials modal) and footer.tsx (Continue
     * gate) through `resolveOnvifStreamVerdict`'s `stalled` input.
     * Per rule: reference-rules-in-comments (ai.knowledge/global).
     */
    onvifProbe?: T_Maybe<Readonly<{ attempt: number; stalled: boolean }>>;

    /** Selected discovery. */
    selection: T_Maybe<Discovery>;
  };

  /** LiveKit token. */
  token?: T_Maybe<Token>;

  /** Video panel state. */
  video: {
    /** Cached configs. */
    cache: T_Optional<{ [K in VideoProtocol]: Minimist['video'] }>;

    /** Ready flag. */
    isReady: boolean;

    /**
     * Empty-creds probe in flight. Set true by rtsp.tsx while the probe machine
     * is PROBE_EMPTY/PROBE_REAL, false at DONE/IDLE. panel.tsx folds `!probing`
     * into its OWN computed isReady and stays the sole writer that raises isReady
     * to true (the reset only clears it to false) — so the credential-FREE
     * empty-probe stream reaching PLAYING can never enable Continue, yet Continue
     * re-enables the moment the probe finishes (which a direct isReady overwrite
     * from rtsp.tsx could not, since panel only re-writes isReady when its own
     * computed value changes).
     */
    probing: boolean;
  };
}> & {
  /** Selected Bridge agent ID. */
  agentId: Agent['id'];

  /** Bridge IDs linked to the selected room. */
  agentIds: Agent['id'][];

  /** Room ID. */
  roomId: Room['id'];
};

/** Workflow store. */
export type Store = UseBoundStoreWithEqualityFn<S.Store<State>>;

/** Workflow panels. */
export enum Panel {
  Source = 1,
  Video,
  Manufacturer,
  Control
}
